import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type SupportedLocale,
  isSupportedLocale
} from '@/lib/i18n/config';
import { persistLanguage } from '@/lib/i18n/detect-language';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type LocaleOption = {
  code: SupportedLocale;
  label: string;
};

type UseLanguageReturn = {
  language: SupportedLocale;
  setLanguage: (locale: SupportedLocale) => void;
  available: ReadonlyArray<LocaleOption>;
};

const AVAILABLE: ReadonlyArray<LocaleOption> = SUPPORTED_LOCALES.map(
  (code) => ({
    code,
    label: LOCALE_LABELS[code]
  })
);

export const useLanguage = (): UseLanguageReturn => {
  const { i18n } = useTranslation();

  const current = isSupportedLocale(i18n.language)
    ? i18n.language
    : DEFAULT_LOCALE;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = current;
    }
  }, [current]);

  const setLanguage = useCallback(
    (locale: SupportedLocale) => {
      if (locale === current) {
        return;
      }
      i18n
        .changeLanguage(locale)
        .then(persistLanguage.bind(null, locale), (error) => {
          if (typeof console !== 'undefined') {
            // biome-ignore lint/suspicious/noConsole: surface unexpected locale-change failures
            console.error('[i18n] failed to change language', {
              locale,
              error
            });
          }
        });
    },
    [current, i18n]
  );

  return {
    language: current,
    setLanguage,
    available: AVAILABLE
  };
};
